import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { useMemo, useState } from "react";
import UserCoworkerQuery, {
  UserCoworkerFilterParameters,
  UserCoworkerOrderField,
  UserCoworkerOrderParameters,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import useInfiniteUserCoworkers from "users/information/coworker-information/_hooks/use-infinite-user-coworkers";
import UserInformation from "users/information/user-information";
import { useGetUserNetworkConnectionsQuery } from "users/networking/connections/_api/user-network-connection-api";
import UpdateNetworkedGroupApiRequest from "users/networking/groups/_api/commands/update-networked-group-api-command";
import { useGetNetworkedGroupByIdQuery } from "users/networking/groups/_api/networked-group-api";
import NetworkedGroupSelector from "users/networking/groups/_views/networked-group-selector";
import NetworkedGroup from "users/networking/groups/networked-group";

const ContentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    columnCount: 1,
  },
  [theme.breakpoints.up("md")]: {
    columnCount: 2,
  },
  [theme.breakpoints.up("lg")]: {
    columnCount: 3,
  },
  columnFill: "balance",
  columnGap: theme.spacing(4),
  gridColumn: "1 / auto",
  gridRowStart: "2",
  paddingTop: theme.spacing(0.75),
}));
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const StyledSubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: "50%",
}));

export interface EditNetworkedGroupProps {
  groupId: Guid;
  onSave: (group?: UpdateNetworkedGroupApiRequest) => void;
}

export default function EditNetworkedGroupComponent(props: Readonly<EditNetworkedGroupProps>) {
  const { groupId, onSave } = props;

  const query = useMemo(
    () =>
      new UserCoworkerQuery(
        new UserCoworkerFilterParameters(),
        new UserCoworkerOrderParameters(UserCoworkerOrderField.Name, OrderDirection.Ascending),
        PaginationParameters.DEFAULT,
      ),
    [],
  );
  const {
    coworkers,
    isLoading: isLoadingCoworkers,
    error: coworkerError,
    loadMore: loadMoreCoworkers,
    retry: retryCoworkers,
  } = useInfiniteUserCoworkers(query);
  const networkConnectionQuery = useGetUserNetworkConnectionsQuery();
  const networkedGroupQuery = useGetNetworkedGroupByIdQuery(groupId);

  const networkConnections = useMemo(() => {
    if (!networkConnectionQuery.data) return [];
    return UserInformation.fromJSONArray(networkConnectionQuery.data)
  }, [networkConnectionQuery.data]);

  const networkedGroup = useMemo(() => {
    if (!networkedGroupQuery.data) return null;
    return NetworkedGroup.fromJSON(networkedGroupQuery.data);
  }, [networkedGroupQuery.data]);

  const [groupName, setGroupName] = useState<string | undefined>(networkedGroup?.name);
  const [description, setDescription] = useState<string | undefined>(networkedGroup?.description);
  const [selectedMembers, setSelectedMembers] = useState<Guid[]>(networkedGroup?.memberUserIds ?? []);
  const [loadedVersion, setLoadedVersion] = useState<string>();

  if (networkedGroup && networkedGroup.version !== loadedVersion) {
    setLoadedVersion(networkedGroup.version);
    setGroupName(networkedGroup.name);
    setDescription(networkedGroup.description);
    setSelectedMembers(networkedGroup.memberUserIds ?? []);
  }

  function generateDefaultGroupName(): string {
    if (selectedMembers.length === 0) return "New Group";
    const selectedMemberNames = selectedMembers.map((id) => {
      const coworker = coworkers.data.find((c) => c.userId.value === id.value);
      const connection = networkConnections.find((c) => c.id.isEqualTo(id));
      return coworker?.name ?? connection?.name ?? "Unknown User";
    });
    return `Chat with ${selectedMemberNames.join(", ")}`;
  }

  function groupHasChanged(): boolean {
    if (!networkedGroup) return false;
    const defaultName = generateDefaultGroupName();
    const currentName = groupName || defaultName;
    if (networkedGroup.name !== currentName) return true;
    if (networkedGroup.description !== description) {
      return (networkedGroup.description ?? "") !== (description ?? "");
    }
    return (
      (networkedGroup.memberUserIds?.length ?? 0) !== selectedMembers.length ||
      !(networkedGroup.memberUserIds ?? []).every((id) => selectedMembers.some((i) => i.value === id.value))
    );
  }

  return (
    <div>
      <ContentContainer>
        <StyledTextField
          label="Group Name"
          variant="outlined"
          value={groupName ?? ""}
          onChange={(e) => setGroupName(e.target.value)}
        />
        <StyledTextField
          label="Description"
          variant="outlined"
          multiline
          rows={4}
          value={description ?? ""}
          onChange={(e) => setDescription(e.target.value)}
        />
        <NetworkedGroupSelector
          coworkers={coworkers.data}
          networkConnections={networkConnections ?? []}
          selectedMemberUserIds={selectedMembers}
          coworkerHasNextPage={coworkers.hasNextPage}
          isLoadingCoworkers={isLoadingCoworkers}
          coworkerError={coworkerError}
          onLoadMoreCoworkers={loadMoreCoworkers}
          onRetryCoworkers={retryCoworkers}
          onMemberSelected={(id: Guid) => {
            setSelectedMembers((prev) =>
              prev.some((i) => i.value === id.value)
                ? prev.filter((memberId) => memberId.value !== id.value)
                : [...prev, id]
            );
          }}
        />
      </ContentContainer>
      <StyledSubmitButton
        variant="contained"
        color="primary"
        disabled={!groupHasChanged() || (groupName !== undefined && groupName.trim().length === 0)}
        onClick={() => {
          if (!networkedGroup) return;
          const defaultName = generateDefaultGroupName();
          const request = new UpdateNetworkedGroupApiRequest(
            networkedGroup,
            new NetworkedGroup({
              id: networkedGroup.id, 
              name: groupName?.trim() || defaultName,
              description: description?.trim() || undefined,
              memberUserIds: selectedMembers, 
              version: networkedGroup.version,
              created: new AHDate()
            })
          );
          onSave(request);
        }}
      >
        Update Group
      </StyledSubmitButton>
    </div>
  );
}
